// src/services/memorySessionManager.ts

import { v4 as uuidv4 } from "uuid";
import {
  AgentSessionSnapshot,
  ChatSession,
  ChatMessage,
  ChatSessionManager,
  InlineToolEvent,
  StaleApplyAttempt,
} from "../interfaces/chatSession";
import { FormRequestPayload, FormField } from "../interfaces/formTypes";
import { WebSearchEvent } from "../interfaces/webSearchTypes";
import { WebFetchToolEvent } from "../interfaces/webToolTypes";
import { NormalizedPlaybookResult } from "../interfaces/playbookTypes";
import { NormalizedCatylexSearchResult } from "../interfaces/catylexSearchTypes";
import {
  NormalizedSharePointResult,
  NormalizedSharePointSearch,
  NormalizedSharePointRead,
} from "../interfaces/sharepointSearchTypes";
import { OutlookMailResult } from "../interfaces/outlookMailTypes";
import { AutoRedlineOutput } from "../interfaces/autoRedlineTypes";
import { createLogger } from "../utils/logger";
import { track } from "../utils/analytics";

const log = createLogger("MemorySessionManager");

/**
 * Synthesize a human-readable summary of a form action for chat history
 *
 * This creates a textual representation of the form that was displayed,
 * allowing the LLM to "remember" that it triggered a workflow form and
 * what fields were pre-filled.
 *
 * @param payload The form request payload from the LLM tool
 * @param submittedData Optional - the actual values submitted by the user
 * @param wasSubmitted Whether the form was submitted
 * @returns A formatted string summarizing the form action
 */
function synthesizeFormActionSummary(
  payload: FormRequestPayload,
  submittedData?: Record<string, string>,
  wasSubmitted?: boolean
): string {
  const formTitle = payload.display.title || payload.formType;

  // Build pre-filled fields summary
  const prefilledFields = payload.fields
    .filter((field: FormField) => field.value && field.source !== "default")
    .map((field: FormField) => {
      const sourceLabel =
        field.source === "document"
          ? "from document"
          : field.source === "conversation"
            ? "from chat"
            : field.source === "inferred"
              ? "inferred"
              : "";
      const sourceInfo = sourceLabel ? ` (${sourceLabel})` : "";
      return `  - ${field.label}: "${field.value}"${sourceInfo}`;
    });

  // Build required but empty fields summary
  const emptyRequiredFields = payload.fields
    .filter((field: FormField) => field.required && !field.value)
    .map((field: FormField) => `  - ${field.label}`);

  let summary = `[WORKFLOW FORM DISPLAYED: ${formTitle}]\n`;
  summary += `Form ID: ${payload.formInstanceId}\n`;

  if (prefilledFields.length > 0) {
    summary += `\nPre-filled fields:\n${prefilledFields.join("\n")}`;
  }

  if (emptyRequiredFields.length > 0) {
    summary += `\n\nFields requiring user input:\n${emptyRequiredFields.join("\n")}`;
  }

  // If form was submitted and we have the submitted data, show what the user actually submitted
  if (wasSubmitted && submittedData) {
    const submittedFieldsSummary = payload.fields
      .map((field: FormField) => {
        const submittedValue = submittedData[field.id] || "";
        const originalValue = field.value || "";
        const wasEdited = submittedValue !== originalValue;
        const editIndicator = wasEdited ? " ← EDITED" : "";
        return `  - ${field.label}: "${submittedValue}"${editIndicator}`;
      })
      .filter((line) => !line.includes('""')); // Filter out empty values

    summary += `\n\n[FORM SUBMITTED - Final values:]\n${submittedFieldsSummary.join("\n")}`;
  } else {
    summary += `\n\n[User can review, edit, and submit this form]`;
  }

  return summary;
}

/**
 * Synthesize a human-readable summary of web searches for chat history
 *
 * This creates a textual representation of web searches that were performed,
 * allowing the LLM to "remember" what it searched for and reference the results
 * in follow-up responses.
 *
 * @param webSearchEvents The array of web search events from the message
 * @returns A formatted string summarizing the web searches
 */
function synthesizeWebSearchSummary(webSearchEvents: WebSearchEvent[]): string {
  if (!webSearchEvents || webSearchEvents.length === 0) {
    return "";
  }

  const searchSummaries = webSearchEvents.map((event) => {
    const resultsList = event.results
      .map((result, idx) => `    ${idx + 1}. ${result.title} - ${result.url}`)
      .join("\n");

    return `Search ${event.searchNumber}: "${event.query}"\n  Results:\n${resultsList}`;
  });

  return `[WEB SEARCHES PERFORMED]\n${searchSummaries.join("\n\n")}`;
}

/**
 * Synthesize a human-readable summary of web fetch events for chat history
 *
 * This creates a textual representation of web pages that were fetched,
 * allowing the LLM to "remember" what URLs it visited and whether they succeeded.
 * The actual page content is NOT included — only metadata.
 *
 * When fetches failed, a fallback instruction is appended guiding the LLM
 * to use web search as an alternative on follow-up questions.
 *
 * @param webFetchEvents The array of web fetch events from the message
 * @returns A formatted string summarizing the web fetches
 */
function synthesizeWebFetchSummary(webFetchEvents: WebFetchToolEvent[]): string {
  if (!webFetchEvents || webFetchEvents.length === 0) {
    return "";
  }

  const fetchSummaries = webFetchEvents.map((event) => {
    if (event.status === "error") {
      return `Fetch ${event.fetchNumber}: ${event.url} — failed (${event.error_code || "unknown error"})`;
    }
    return `Fetch ${event.fetchNumber}: ${event.title || "Untitled"} — ${event.url}`;
  });

  const hasFailures = webFetchEvents.some((e) => e.status === "error");

  let summary = `[WEB PAGES FETCHED]\n${fetchSummaries.join("\n")}`;

  if (hasFailures) {
    summary +=
      "\n[Note: Some pages could not be fetched directly. If the user asks about content from a failed fetch, try using web search to find the relevant information instead.]";
  }

  return summary;
}

/**
 * Build rich tool_result content for playbook structured blocks.
 * Includes section hierarchy so CS can reference specific playbook sections in follow-ups.
 */
function buildPlaybookToolResultContent(result: NormalizedPlaybookResult): string {
  const { sections, content_length, reason, playbook_title, last_updated } = result;

  let documentTitle = "Company Playbook";
  if (playbook_title) {
    documentTitle = playbook_title;
  } else {
    const firstLevel1 = sections.find((s) => s.level === 1);
    if (firstLevel1) {
      documentTitle = firstLevel1.title;
    }
  }

  const MAX_SECTIONS = 20;
  const sectionLines: string[] = [];
  let currentLevel1: string | null = null;
  const level2Items: string[] = [];
  let sectionCount = 0;

  for (const section of sections) {
    if (sectionCount >= MAX_SECTIONS) break;
    if (section.level === 1) {
      if (currentLevel1 !== null) {
        sectionLines.push(`- ${currentLevel1}`);
        level2Items.forEach((item) => sectionLines.push(`  - ${item}`));
        level2Items.length = 0;
        sectionCount++;
      }
      currentLevel1 = section.title;
    } else if (section.level === 2 && currentLevel1 !== null) {
      level2Items.push(section.title);
      sectionCount++;
    }
  }
  if (currentLevel1 !== null && sectionCount < MAX_SECTIONS) {
    sectionLines.push(`- ${currentLevel1}`);
    level2Items.forEach((item) => sectionLines.push(`  - ${item}`));
  }
  if (sectionLines.length === 0) {
    sections.slice(0, MAX_SECTIONS).forEach((s) => {
      const prefix = s.level === 1 ? "- " : s.level === 2 ? "  - " : "    - ";
      sectionLines.push(`${prefix}${s.title}`);
    });
  }

  let content = `Playbook consulted: ${reason}\nDocument: ${documentTitle}`;
  if (last_updated) {
    content += `\nLast Updated: ${last_updated}`;
  }
  if (sectionLines.length > 0) {
    content += `\nSections:\n${sectionLines.join("\n")}`;
  }
  if (sections.length > MAX_SECTIONS) {
    content += `\n... and ${sections.length - MAX_SECTIONS} more sections`;
  }
  const sizeStr =
    content_length < 1000
      ? `${content_length} characters`
      : content_length < 10000
        ? `${(content_length / 1000).toFixed(1)}K characters`
        : `${Math.round(content_length / 1000)}K characters`;
  content += `\n(${sizeStr} total)`;
  content +=
    `\nNote: This is only a SUMMARY of an earlier load — the full playbook is NOT in context now. ` +
    `Before quoting or citing any playbook provision in a reply, call get_client_playbook again to reload it ` +
    `(citations only resolve for a freshly loaded playbook).`;

  return content;
}

/**
 * Build rich tool_result content for catylex search structured blocks.
 * Includes search results detail so CS can reference specific contracts/sentences in follow-ups.
 *
 * For a read, the loaded document body is replaced with a placeholder. This is
 * the only place Catylex reads shed context: the backend deliberately leaves
 * history untouched mid-turn (rewriting it would invalidate the prompt-cache
 * prefix and re-write the whole tools + system + document head at 1.25x), so
 * cross-turn replacement here is what keeps a long comparison session from
 * re-paying for every contract it has ever loaded.
 */
/**
 * The contract ids from a search, in the same shape the backend emits live
 * (`build_id_manifest` in `src/tools/catylex_search/chunks.py`, whose
 * `seed_read_cache_from_history` parses this text back out of prior turns).
 *
 * Without this the read action is unreachable across turns: the rebuilt result
 * lists filenames only, so a model that searched in turn 1 and is asked to load
 * a hit in turn 2 has nothing to address it by. Ids live on the normalized
 * result already — contracts key off `id`, sentences off `contract_id` (deduped,
 * since many sentences share one contract).
 *
 * The instruction line is carried verbatim from the backend on purpose: bare ids
 * next to titles read as noise, that phrase is what makes them actionable.
 */
function buildCatylexIdManifest(result: NormalizedCatylexSearchResult, maxItems: number): string {
  const rows: Array<[string | undefined, string | undefined]> =
    result.searchType === "sentences"
      ? (result.sentences ?? []).map((s) => [s.contract_id, s.contract_name || s.display_title])
      : (result.contracts ?? []).map((c) => [c.id, c.display_title || c.title]);

  const seen = new Set<string>();
  const lines: string[] = [];
  for (const [contractId, label] of rows) {
    if (!contractId || seen.has(contractId)) continue;
    seen.add(contractId);
    if (lines.length >= maxItems) break;
    const trimmed = (label || "").trim();
    lines.push(trimmed ? `- ${contractId} — ${trimmed}` : `- ${contractId}`);
  }

  if (!lines.length) return "";

  return `\n\nContracts you can load in full (use action="read" with contract_id):\n${lines.join("\n")}`;
}

function buildCatylexToolResultContent(result: NormalizedCatylexSearchResult): string {
  const MAX_ITEMS = 10;

  if (result.status === "error" && result.action === "read") {
    const label = result.displayTitle || result.fileName || result.contractId || "contract";
    return (
      `Loading contract ${label} from Catylex failed: ${result.errorMessage || "the contract could not be loaded"}` +
      (result.errorCode ? ` (${result.errorCode})` : "")
    );
  }

  // Status is checked as well as action: this branch tells the model a document
  // was loaded, so it must not be reachable by any non-success read (a
  // "not_supported" read would otherwise serialize as "0 sentences" loaded).
  if (result.status === "success" && result.action === "read") {
    const label = result.displayTitle || result.fileName || result.contractId || "contract";
    let content = `Catylex contract loaded: ${label}`;
    if (result.contractId) {
      content += `\ncontract_id: ${result.contractId}`;
    }
    content += `\nContent loaded: ${result.sentencesReturned ?? 0} sentences`;
    content +=
      `\nNote: The full contract text was available during the original response but has been ` +
      `removed from history to manage context length. If you still need the text, call ` +
      `catylex_search with action "read" and this contract_id again.`;
    if (result.truncated && result.totalSentences != null) {
      content += `\nOnly part of the contract had been loaded (of ${result.totalSentences} sentences total).`;
    }
    return content;
  }

  if (result.status === "not_supported") {
    let content = `Search for "${result.originalQuery}": not supported.`;
    if (result.reason) {
      content += ` Reason: ${result.reason}`;
    }
    if (result.suggestions?.length) {
      const texts = result.suggestions.map((s) => (typeof s === "string" ? s : s.suggestion_text));
      content += ` Suggestions: Try "${texts.join('", "')}"`;
    }
    return content;
  }

  if (result.status === "error") {
    return `Search for "${result.originalQuery}": error — ${result.errorMessage || "Search could not be completed"}`;
  }

  // Must precede the success tail, and this is the whole reason the branch
  // exists: a skipped search has no results to fall through with, so it would
  // serialize as `Results: 0 results` — telling the model on the next turn that
  // a query it never ran came back empty. Purely factual on purpose: the "ask
  // the user first" instruction the backend sent belongs to the response that
  // was capped, and a later turn gets its own search.
  if (result.status === "skipped") {
    return (
      `Search for "${result.originalQuery}": not run — Catylex had already been ` +
      `searched in that response, so this query was never sent.`
    );
  }

  // Success
  let content = `Search for "${result.originalQuery}"`;
  if (result.interpretedQuery && result.interpretedQuery !== result.originalQuery) {
    content += ` (interpreted as: "${result.interpretedQuery}")`;
  }
  if (result.searchType) {
    content += `\nType: ${result.searchType}`;
  }
  content += `\nResults: ${result.resultsSummary || `${result.totalResults || 0} results`}`;

  if (result.searchType === "contracts" && result.contracts?.length) {
    const shown = result.contracts.slice(0, MAX_ITEMS);
    shown.forEach((c, i) => {
      content += `\n  ${i + 1}. ${c.title} (${c.status})`;
    });
  } else if (result.searchType === "sentences" && result.sentences?.length) {
    const shown = result.sentences.slice(0, MAX_ITEMS);
    shown.forEach((s, i) => {
      const pageInfo = s.pages?.length ? ` (page ${s.pages.join(", ")})` : "";
      const snippet = s.text.length > 80 ? `${s.text.slice(0, 80)}...` : s.text;
      content += `\n  ${i + 1}. "${snippet}" — ${s.contract_name}${pageInfo}`;
    });
  }

  content += buildCatylexIdManifest(result, MAX_ITEMS);

  return content;
}

/**
 * Build rich tool_result content for SharePoint search structured blocks.
 * Includes search/read result detail so the LLM can reference documents in follow-ups.
 */
function buildSharePointToolResultContent(result: NormalizedSharePointResult): string {
  if (result.status === "error") {
    return (
      `SharePoint ${result.action} failed: ${result.errorMessage} (${result.errorCode})` +
      `\nYou can retry the same action to attempt recovery.`
    );
  }

  if (result.action === "search") {
    const searchResult = result as NormalizedSharePointSearch;
    let content = `SharePoint search for "${searchResult.originalQuery}"`;
    content += `\nResults: ${searchResult.totalResults} found, showing ${searchResult.showing}`;
    if (searchResult.moreResultsAvailable) {
      content += ` (more available)`;
    }
    if (searchResult.files.length > 0) {
      const fileLines = searchResult.files.map(
        (f, i) =>
          `  ${i + 1}. ${f.name} (${f.mime_type}) — drive_id: ${f.drive_id}, item_id: ${f.item_id}`
      );
      content += `\nFiles:\n${fileLines.join("\n")}`;
      content += `\nTo read a file's content, call sharepoint_search with action "read" using the file's drive_id and item_id.`;
    }
    return content;
  }

  // action === "read"
  const readResult = result as NormalizedSharePointRead;
  const total = readResult.totalContentLength || 1;
  const startPct = Math.min(100, Math.round((readResult.offset / total) * 100));
  const endPct = Math.min(
    100,
    Math.round(((readResult.offset + readResult.charsReturned) / total) * 100)
  );
  const range = startPct === 0 && endPct === 100 ? "100%" : `${startPct}%–${endPct}%`;

  let content = `SharePoint document read: ${readResult.fileName}`;
  content += `\nContent loaded: ${range} of the document (${readResult.charsReturned} chars)`;
  content += `\ndrive_id: ${readResult.driveId}, item_id: ${readResult.itemId}`;
  content += `\nNote: The full document content was available during the original response but has been removed from history to manage context length. If you need to reference this document again, call for it again.`;
  if (readResult.truncated) {
    content += `\nThe document was partially loaded. To read the next section, use offset=${readResult.nextOffset}.`;
  }
  return content;
}

type HistoryMessage = { role: string; content: string | unknown[] };

/**
 * Build tool_result content for Outlook mail structured blocks.
 * Includes action, status, and key fields so the LLM can reference prior email actions.
 */
function buildOutlookMailToolResultContent(result: OutlookMailResult): string {
  if (result.status === "error") {
    return (
      `Outlook mail ${result.action} failed: ${result.errorMessage} (${result.errorCode})` +
      `\nYou can retry the same action to attempt recovery.`
    );
  }

  if (result.status === "preview") {
    let content = `Outlook mail preview generated`;
    content += `\nSubject: ${result.subject}`;
    content += `\nTo: ${result.toRecipients.join(", ")}`;
    if (result.ccRecipients.length > 0) {
      content += `\nCC: ${result.ccRecipients.join(", ")}`;
    }
    if (result.attachOpenDocument) {
      content += `\nAttachment: ${result.attachmentName || "open document"} (pending user confirmation)`;
    }
    if (result.bodyPreview) {
      const plainText = result.bodyPreview
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/g, " ")
        .replace(/&amp;/g, "&")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/\s+/g, " ")
        .trim();
      content += `\nBody: ${plainText}`;
    }
    return content;
  }

  // status === "success"
  const actionLabel = result.action === "send" ? "Email sent" : "Draft created";
  let content = `${actionLabel} successfully`;
  content += `\nSubject: ${result.subject}`;
  content += `\nTo: ${result.toRecipients.join(", ")}`;
  if (result.ccRecipients.length > 0) {
    content += `\nCC: ${result.ccRecipients.join(", ")}`;
  }
  if (result.hasAttachment && result.attachmentName) {
    content += `\nAttachment: ${result.attachmentName}`;
  }
  if (result.messageId) {
    content += `\nMessage ID: ${result.messageId}`;
  }
  return content;
}

/** Max characters of paragraph text quoted as a locator in a status line. */
const TEXT_ANCHOR_MAX_CHARS = 60;

/**
 * Normalize paragraph text into a short quoted locator for tool_result status
 * lines.
 *
 * Text is the ONLY handle that survives into the next request. The backend
 * re-parses the live document at the start of every request and position ids
 * (`p63`) are ordinals over that parse, so any id we write into conversation
 * history points at a different paragraph as soon as the user applies, undoes,
 * or accepts/rejects a change. The model re-locates the paragraph by matching
 * this quote in the freshly parsed document it is given.
 *
 * Double quotes are folded to single so the quote can't run past its own
 * delimiters; exact character fidelity is not required, since applied
 * insertions are re-parsed wrapped in `<inserted; Author; Date>` markup and are
 * matched approximately anyway.
 *
 * Returns undefined when there is nothing usable, so callers drop the clause
 * instead of emitting an empty quote.
 */
function buildTextAnchor(text: unknown): string | undefined {
  if (typeof text !== "string") return undefined;
  const normalized = text.replace(/\s+/g, " ").trim().replace(/"/g, "'");
  if (!normalized) return undefined;
  if (normalized.length <= TEXT_ANCHOR_MAX_CHARS) return normalized;
  const cut = normalized.slice(0, TEXT_ANCHOR_MAX_CHARS);
  const lastSpace = cut.lastIndexOf(" ");
  return `${(lastSpace > 0 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}

/**
 * Describe where applied content now lives, using text rather than any index.
 *
 * `output.paragraphId` is a 0-based internal Word body index used by the apply
 * pipeline — it is NOT a position id, and phrasing it as "paragraph 62" made
 * the model target `p62` on the next turn. It is deliberately absent here; see
 * buildTextAnchor for what replaces it.
 */
function buildAppliedLocation(output: AutoRedlineOutput): string | undefined {
  const input = output.tool_input ?? {};
  const newText = buildTextAnchor(input.new_text);
  const targetText = buildTextAnchor(output.searchParagraph);

  if (output.operationType === "insert") {
    // searchParagraph is the ANCHOR paragraph for inserts — the one the new
    // paragraph was placed relative to. tool_input carries only its id.
    const where = input.insert_position === "before" ? "before" : "after";
    const anchorClause = targetText ? ` ${where} the paragraph starting "${targetText}"` : "";
    if (!newText) {
      return anchorClause ? `The new paragraph sits${anchorClause}.` : undefined;
    }
    return `The new paragraph sits${anchorClause}, and its text starts: "${newText}".`;
  }

  if (output.operationType === "delete") {
    if (!targetText) return undefined;
    return `The deleted paragraph starts "${targetText}" and stays visible with deletion markup until the user accepts the deletion in Word's Review tab.`;
  }

  // edit — new_text is the full replacement, so it is what the paragraph reads
  // now. Structure-only edits send no new_text; fall back to the original.
  const editedText = newText ?? targetText;
  return editedText ? `That paragraph now starts: "${editedText}".` : undefined;
}

/**
 * Reflect per-edit apply outcomes back to the model so it knows whether the
 * suggestions are still pending user review, were applied by the user, or
 * failed to apply (addComment pattern). Outputs without editStatuses (old
 * sessions, or previews the user never acted on) read as pending.
 *
 * These lines carry FACTS only — status plus a text locator. The rule for how
 * to use that locator (re-find the paragraph in the current document, take the
 * position id from there) lives once in the backend tool schema's Suggestion
 * Lifecycle section, not repeated in every tool_result.
 */
function buildAutoRedlineToolResultContent(output: AutoRedlineOutput): string {
  const total = output.edits.length;
  let applied = 0;
  let failed = 0;
  let firstError: string | undefined;
  for (const edit of output.edits) {
    const outcome = output.editStatuses?.[edit.editId];
    if (outcome?.status === "applied") {
      applied++;
    } else if (outcome?.status === "failed") {
      failed++;
      if (!firstError && outcome.errorMessage) firstError = outcome.errorMessage;
    }
  }
  const pending = total - applied - failed;

  if (applied === 0 && failed === 0) {
    return `${total} suggested edit(s) for "${output.sectionName}" were staged for user review. The user has not applied them to the document yet.`;
  }

  const location = buildAppliedLocation(output);
  const locationClause = location ? ` ${location}` : "";

  if (applied === total) {
    return `The user clicked apply and all ${total} suggested edit(s) for "${output.sectionName}" were successfully applied to the document as tracked changes.${locationClause} This suggestion is no longer pending.`;
  }
  if (failed === total) {
    return `The user tried to apply the ${total} suggested edit(s) for "${output.sectionName}", but they failed to apply and are NOT in the document${firstError ? ` (error: ${firstError})` : ""}.`;
  }
  const parts: string[] = [];
  if (applied > 0) {
    parts.push(`the user applied ${applied} (now in the document as tracked changes)`);
  }
  if (failed > 0) {
    parts.push(`${failed} failed to apply${firstError ? ` (error: ${firstError})` : ""}`);
  }
  if (pending > 0) {
    parts.push(`${pending} still awaiting user review (not applied)`);
  }
  return `Of the ${total} suggested edit(s) for "${output.sectionName}": ${parts.join(", ")}.${locationClause}`;
}

/**
 * Build tool_use and tool_result blocks from a single inline tool event.
 * Pushes blocks into the provided arrays in-place.
 */
function buildToolBlocksForEvent(
  event: InlineToolEvent,
  toolUseBlocks: unknown[],
  toolResultBlocks: unknown[]
): void {
  switch (event.type) {
    case "playbook": {
      const r = event.data.playbookResult;
      if (!r?.tool_use_id) return;
      toolUseBlocks.push({
        type: "tool_use",
        id: r.tool_use_id,
        name: "get_client_playbook",
        input: { reason: r.reason },
      });
      toolResultBlocks.push({
        type: "tool_result",
        tool_use_id: r.tool_use_id,
        content: buildPlaybookToolResultContent(r),
      });
      break;
    }
    case "playbookSelection": {
      // The playbook list that was shown to the user (call #1). Close that
      // tool_use with a tool_result naming the choice, so on the next turn the
      // model makes a fresh call #2 to load the chosen playbook (which dispatches
      // normally — citations register on call #2's content, not on this list).
      const sel = event.data.playbookSelection;
      const r = sel?.result;
      if (!r?.tool_use_id) return;
      toolUseBlocks.push({
        type: "tool_use",
        id: r.tool_use_id,
        name: "get_client_playbook",
        input: { reason: r.reason },
      });
      const content =
        sel?.selected && sel.selectedId
          ? `The user selected the "${sel.selectedName ?? "chosen"}" playbook (id ${sel.selectedId}) from the list. Call get_client_playbook again now to load it, then continue with the user's request.`
          : "The playbook list was shown to the user; awaiting their selection.";
      toolResultBlocks.push({
        type: "tool_result",
        tool_use_id: r.tool_use_id,
        content,
      });
      break;
    }
    case "catylexSearch": {
      const r = event.data.catylexSearchResult;
      if (!r?.tool_use_id) return;
      toolUseBlocks.push({
        type: "tool_use",
        id: r.tool_use_id,
        name: "catylex_search",
        // `action` is required by the tool schema, so reconstruct it rather than
        // replaying a search-shaped input for what may have been a read.
        input:
          r.action === "read"
            ? { action: "read", contract_id: r.contractId }
            : { action: "search", query: r.originalQuery },
      });
      toolResultBlocks.push({
        type: "tool_result",
        tool_use_id: r.tool_use_id,
        content: buildCatylexToolResultContent(r),
      });
      break;
    }
    case "autoRedline": {
      for (const output of event.data.autoRedlineOutputs || []) {
        if (!output.tool_use_id || !output.tool_input) continue;
        toolUseBlocks.push({
          type: "tool_use",
          id: output.tool_use_id,
          name: "auto_redline_tool",
          input: output.tool_input,
        });
        toolResultBlocks.push({
          type: "tool_result",
          tool_use_id: output.tool_use_id,
          content: buildAutoRedlineToolResultContent(output),
        });
      }
      break;
    }
    case "addComment": {
      const c = event.data.addComment;
      const r = c?.result;
      if (!r?.tool_use_id || !r.tool_input) return;
      toolUseBlocks.push({
        type: "tool_use",
        id: r.tool_use_id,
        name: "add_comment_tool",
        input: r.tool_input,
      });

      // Reflect the per-card state back to the model so it knows whether the
      // comment landed, was blocked, failed, or is still awaiting approval.
      let content: string;
      switch (c?.status) {
        case "applied":
          content = `The comment for "${r.sectionName}" was approved by the user and has been inserted into the document.`;
          break;
        case "failed":
          content = `The comment for "${r.sectionName}" was approved but could not be inserted into the document${
            c?.errorMessage ? ` (error: ${c.errorMessage})` : ""
          }.`;
          break;
        case "stale":
          content = `The comment for "${r.sectionName}" was NOT inserted: the target text changed since this comment was prepared, so the apply was blocked. Regenerate the comment against the current text if the user still wants it.`;
          break;
        default:
          // "pending" / "applying" — shown for approval, not yet applied.
          content = `A comment for "${r.sectionName}" was shown to the user for approval. The user has not applied it.`;
          break;
      }
      toolResultBlocks.push({
        type: "tool_result",
        tool_use_id: r.tool_use_id,
        content,
      });
      break;
    }
    case "sharepointSearch": {
      const r = event.data.sharepointSearchResult;
      if (!r?.toolUseId || !("toolInput" in r) || !r.toolInput) return;
      toolUseBlocks.push({
        type: "tool_use",
        id: r.toolUseId,
        name: "sharepoint_search",
        input: r.toolInput,
      });
      toolResultBlocks.push({
        type: "tool_result",
        tool_use_id: r.toolUseId,
        content: buildSharePointToolResultContent(r),
      });
      break;
    }
    case "outlookMail": {
      const r = event.data.outlookMailResult;
      if (!r) return;

      // Direct sends (from the preview card) are NOT LLM tool calls —
      // they should not produce tool_use/tool_result blocks.
      // Instead, they are handled as text annotations by buildInterleavedMessages.
      if (r.source === "direct") return;

      // LLM-originated results: serialize as tool_use + tool_result
      if (!r.toolUseId) return;
      toolUseBlocks.push({
        type: "tool_use",
        id: r.toolUseId,
        name: "outlook_mail",
        input: {
          action: r.action,
          subject: r.subject,
          to: r.toRecipients,
          ...(r.ccRecipients.length > 0 && { cc: r.ccRecipients }),
          ...(r.attachOpenDocument != null && {
            attach_open_document: r.attachOpenDocument,
          }),
        },
      });
      toolResultBlocks.push({
        type: "tool_result",
        tool_use_id: r.toolUseId,
        content: buildOutlookMailToolResultContent(r),
      });
      break;
    }
  }
}

/**
 * Synthesize a textual note describing one or more AutoRedline previews whose
 * apply was blocked because the target paragraph had been edited since the
 * preview was generated. Appended to the assistant message suffix so the LLM
 * sees the blocked-apply context on the next user turn.
 *
 * The wording is deliberately directive: it states the single concrete cause,
 * provides a plain-language sample reply, and explicitly forbids speculating
 * about tracked-change markers / batch state / position IDs / other system
 * internals (the LLM tends to invent technical-sounding causes from document
 * context if not warned).
 */
function synthesizeStaleApplyAttemptsSummary(attempts: StaleApplyAttempt[]): string {
  const sectionList = attempts.map((a) => `"${a.sectionName}"`).join(", ");
  const noun = attempts.length === 1 ? "preview" : "previews";

  return (
    `[AutoRedline apply blocked for ${attempts.length} ${noun}: ${sectionList}. ` +
    `Cause (the only one): the user manually edited those exact paragraphs ` +
    `in Word after the suggestions were generated, so the original wording ` +
    `the edits were anchored to no longer matched the live document. ` +
    `Applying would have landed the changes in the wrong place. ` +
    `If the user asks why some edits weren't applied or asks you to redo ` +
    `them, answer conversationally in plain everyday language — no bullet ` +
    `points, no headers, no technical terms. Sample reply: ` +
    `"I couldn't apply the edits for [section names] because you made ` +
    `changes to those paragraphs after I generated the suggestions, so my ` +
    `original wording no longer matched. Want me to redo those suggestions ` +
    `against the current text?" Do not invent other causes (tracked-change ` +
    `markers, batch ordering, document internals, position IDs, system ` +
    `mechanisms) — those are not the reason.]`
  );
}

/**
 * Split an assistant message's concatenated text at inlineToolEvent positions
 * and interleave tool_use/tool_result blocks for proper Anthropic API structure.
 *
 * Returns the alternating assistant/user messages plus any trailing tool_results
 * that need to be merged into the next user message by the caller.
 */
function buildInterleavedMessages(message: ChatMessage): {
  messages: HistoryMessage[];
  trailingToolResults: unknown[];
} {
  // Build text augmentation suffix (non-tool, non-web metadata appended to final text)
  let suffix = "";
  if (message.wasInterrupted) {
    suffix += "\n\n[Note: This response was intentionally interrupted by the user]";
  }
  if (message.formRequestPayload) {
    suffix += `\n\n${synthesizeFormActionSummary(message.formRequestPayload, message.formSubmittedData, message.formSubmitted)}`;
  }
  if (message.staleApplyAttempts && message.staleApplyAttempts.length > 0) {
    suffix += `\n\n${synthesizeStaleApplyAttemptsSummary(message.staleApplyAttempts)}`;
  }

  // Direct-send outlook mail results are user-initiated from the preview card,
  // not LLM tool calls. Serialize as text annotations in the suffix.
  for (const event of message.inlineToolEvents || []) {
    if (event.type !== "outlookMail") continue;
    const r = event.data.outlookMailResult;
    if (!r || r.source !== "direct") continue;
    const actionLabel = r.action === "send" ? "sent" : "saved as draft";
    let annotation = `\n\n[User ${actionLabel} the email from the preview card.`;
    annotation += ` Subject: "${r.subject}".`;
    annotation += ` To: ${r.toRecipients.join(", ")}.`;
    if (r.ccRecipients.length > 0) {
      annotation += ` CC: ${r.ccRecipients.join(", ")}.`;
    }
    if (r.hasAttachment && r.attachmentName) {
      annotation += ` Attachment: ${r.attachmentName}.`;
    }
    if (r.bodyPreview) {
      const plainBody = r.bodyPreview
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/g, " ")
        .replace(/&amp;/g, "&")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/\s+/g, " ")
        .trim();
      annotation += ` Body: ${plainBody}`;
    }
    annotation += "]";
    suffix += annotation;
  }

  // Collect positioned web summaries from inlineToolEvents
  const webSummaries: { position: number; text: string }[] = [];
  for (const event of message.inlineToolEvents || []) {
    if (event.type !== "webTools") continue;
    let text = "";
    if (event.data.webSearchEvents?.length) {
      text += synthesizeWebSearchSummary(event.data.webSearchEvents);
    }
    if (event.data.webFetchEvents?.length) {
      if (text) text += "\n\n";
      text += synthesizeWebFetchSummary(event.data.webFetchEvents);
    }
    if (text) {
      webSummaries.push({ position: event.position, text });
    }
  }
  webSummaries.sort((a, b) => a.position - b.position);

  // Fallback: if no positioned web summaries, use top-level events (old messages)
  if (webSummaries.length === 0) {
    if (message.webSearchEvents?.length) {
      suffix += `\n\n${synthesizeWebSearchSummary(message.webSearchEvents)}`;
    }
    if (message.webFetchEvents?.length) {
      suffix += `\n\n${synthesizeWebFetchSummary(message.webFetchEvents)}`;
    }
  }

  // Helper: inject web summaries into a text range [from, to) of message.content
  const injectWebSummaries = (from: number, to: number): string => {
    const relevant = webSummaries.filter((ws) => ws.position >= from && ws.position < to);
    if (relevant.length === 0) return message.content.slice(from, to);
    let result = "";
    let pos = from;
    for (const ws of relevant) {
      result += message.content.slice(pos, ws.position);
      result += `\n\n${ws.text}\n\n`;
      pos = ws.position;
    }
    result += message.content.slice(pos, to);
    return result;
  };

  // Filter to actionable events: tools that produce tool_use/tool_result blocks +
  // thinking segments that need to be serialized thinking blocks.
  // (skip toolProcessing and webTools — handled above)
  const actionableTypes = new Set([
    "playbook",
    "playbookSelection",
    "autoRedline",
    "catylexSearch",
    "sharepointSearch",
    "outlookMail",
    "addComment",
    "thinking",
  ]);
  const events = (message.inlineToolEvents || [])
    .filter((e) => actionableTypes.has(e.type))
    .sort((a, b) => a.position - b.position || a.timestamp - b.timestamp);

  // Discriminated union: thinking blocks accumulate into the current assistant
  // content; tool events flush the current message and emit a paired user
  // tool_result message.
  type PreparedEvent =
    | { kind: "tool"; event: InlineToolEvent; useBlocks: unknown[]; resultBlocks: unknown[] }
    | { kind: "thinking"; event: InlineToolEvent; block: unknown };

  // Diagnostic counters: when a thinking block is dropped while rebuilding history,
  // the reconstructed assistant turn no longer matches what the model originally
  // returned — the class of mismatch the backend then has to recover from.
  // Observability only; the event is emitted once after the loop, no behavior change.
  let droppedNoSignature = 0;
  let droppedNoContent = 0;

  const prepared: PreparedEvent[] = [];
  for (const event of events) {
    if (event.type === "thinking") {
      const thinking = event.data.thinking;
      // Skip if missing signature or content — Anthropic API rejects incomplete
      // thinking blocks. A missing signature indicates the BE didn't propagate
      // it (e.g., older response) or the segment was interrupted mid-stream.
      if (!thinking?.signature || !thinking.content) {
        if (!thinking?.signature) droppedNoSignature += 1;
        else droppedNoContent += 1;
        continue;
      }
      prepared.push({
        kind: "thinking",
        event,
        block: {
          type: "thinking",
          thinking: thinking.content,
          signature: thinking.signature,
        },
      });
      continue;
    }
    const useBlocks: unknown[] = [];
    const resultBlocks: unknown[] = [];
    buildToolBlocksForEvent(event, useBlocks, resultBlocks);
    if (useBlocks.length === 0) continue;
    prepared.push({ kind: "tool", event, useBlocks, resultBlocks });
  }

  // Telemetry: a dropped thinking block means the rebuilt history turn diverges
  // from the original response. Lets us measure how often this happens and via
  // which path, and correlate it with the backend recovery.
  const droppedThinkingBlocks = droppedNoSignature + droppedNoContent;
  if (droppedThinkingBlocks > 0) {
    const totalThinkingBlocks = events.filter((e) => e.type === "thinking").length;
    track("chat:history_thinking_dropped", {
      message_id: message.id,
      dropped_thinking_blocks: droppedThinkingBlocks,
      dropped_no_signature: droppedNoSignature,
      dropped_no_content: droppedNoContent,
      total_thinking_blocks: totalThinkingBlocks,
      kept_thinking_blocks: totalThinkingBlocks - droppedThinkingBlocks,
    });
  }

  // No actionable events → single assistant message with web summaries at position
  if (prepared.length === 0) {
    const text = injectWebSummaries(0, message.content.length) + suffix;
    return { messages: [{ role: "assistant", content: text }], trailingToolResults: [] };
  }

  // Split-and-interleave: text segments + thinking blocks accumulate into
  // currentAssistantContent. Tool events append their useBlocks then flush
  // (push assistant message + paired user tool_result message).
  const messages: HistoryMessage[] = [];
  let currentAssistantContent: unknown[] = [];
  let lastSplitPos = 0;

  for (const p of prepared) {
    // Push text segment between last split and this event's position
    const segmentText = injectWebSummaries(lastSplitPos, p.event.position);
    if (segmentText.length > 0) {
      currentAssistantContent.push({ type: "text", text: segmentText });
    }
    lastSplitPos = p.event.position;

    if (p.kind === "thinking") {
      currentAssistantContent.push(p.block);
      continue;
    }
    // Tool event: append useBlocks, flush assistant + emit paired user tool_result
    currentAssistantContent.push(...p.useBlocks);
    messages.push({ role: "assistant", content: currentAssistantContent });
    messages.push({ role: "user", content: p.resultBlocks });
    currentAssistantContent = [];
  }

  // Final text segment (use Infinity to capture any trailing web summaries)
  const trailingText = injectWebSummaries(lastSplitPos, message.content.length + 1) + suffix;
  if (trailingText.length > 0) {
    currentAssistantContent.push({ type: "text", text: trailingText });
  }

  if (currentAssistantContent.length > 0) {
    messages.push({ role: "assistant", content: currentAssistantContent });
    return { messages, trailingToolResults: [] };
  }

  // No trailing content — last user message (tool_results) becomes trailing
  const lastUserMsg = messages.pop();
  if (!lastUserMsg) {
    log.error("Invariant violated: expected trailing user message in interleaved history", {
      preparedCount: prepared.length,
      messagesCount: messages.length,
    });
    return { messages, trailingToolResults: [] };
  }
  return { messages, trailingToolResults: lastUserMsg.content as unknown[] };
}

/**
 * In-memory implementation of the ChatSessionManager with multi-agent support
 * Each agent maintains its own isolated conversation history
 * Sessions persist during agent switches but are cleared when the add-in is closed
 */
export class MemorySessionManager implements ChatSessionManager {
  // Map of agent ID to chat session
  private sessions: Map<string, ChatSession> = new Map();
  // Per-agent selected playbook (Catylex multi-playbook tenants). Kept in a
  // sidecar map (not on ChatSession) so it isn't serialized into history and
  // can be reset independently of contract type.
  private selectedPlaybooks: Map<string, { id: string; name: string }> = new Map();
  private currentAgentId: string = "red-line-analyzer";

  constructor() {
    // Initialize with no sessions - they'll be created on demand
  }

  /**
   * Set the current agent ID to switch session contexts
   * @param agentId The agent ID to switch to
   */
  setCurrentAgent(agentId: string): void {
    this.currentAgentId = agentId;

    // Initialize session for this agent if it doesn't exist
    if (!this.sessions.has(agentId)) {
      this.sessions.set(agentId, {
        messages: [],
        lastUpdated: Date.now(),
        contractType: undefined,
        seenTipIds: new Set<string>(),
      });
    }
  }

  /**
   * Get the current agent's session
   * Creates a new session if one doesn't exist for the current agent
   */
  getCurrentSession(): ChatSession {
    if (!this.sessions.has(this.currentAgentId)) {
      this.sessions.set(this.currentAgentId, {
        messages: [],
        lastUpdated: Date.now(),
        contractType: undefined,
        seenTipIds: new Set<string>(),
      });
    }
    return this.sessions.get(this.currentAgentId)!;
  }

  /**
   * Add a message to the current agent's session
   * @param message Message to add (without ID)
   * @returns Complete message with generated ID
   */
  addMessage(message: Omit<ChatMessage, "id">): ChatMessage {
    const newMessage: ChatMessage = {
      ...message,
      id: uuidv4(),
      timestamp: message.timestamp || Date.now(),
    };

    const currentSession = this.getCurrentSession();

    // CRITICAL: Create a new session object to trigger React updates
    // This ensures React detects the change at all levels
    this.sessions.set(this.currentAgentId, {
      messages: [...currentSession.messages, newMessage],
      lastUpdated: Date.now(),
      contractType: currentSession.contractType,
      seenTipIds: currentSession.seenTipIds,
    });

    return newMessage;
  }

  /**
   * Update an existing message in the current agent's session
   * @param id Message ID to update
   * @param updates Partial message updates
   */
  updateMessage(id: string, updates: Partial<Omit<ChatMessage, "id">>): void {
    const currentSession = this.getCurrentSession();
    const messageIndex = currentSession.messages.findIndex((msg) => msg.id === id);

    if (messageIndex !== -1) {
      // CRITICAL: Create completely new objects at every level
      // This ensures React's shallow comparison detects the change

      // Create a new message object with updates
      const updatedMessage = {
        ...currentSession.messages[messageIndex],
        ...updates,
        id, // Ensure ID doesn't change
      };

      // Create a new messages array
      const updatedMessages = [...currentSession.messages];
      updatedMessages[messageIndex] = updatedMessage;

      // Create a new session object
      // This is crucial for React to detect the change
      this.sessions.set(this.currentAgentId, {
        messages: updatedMessages,
        lastUpdated: Date.now(),
        contractType: currentSession.contractType,
        seenTipIds: currentSession.seenTipIds,
      });
    } else {
      console.warn("MemorySessionManager: Message not found for update", {
        id,
        agentId: this.currentAgentId,
      });
    }
  }

  /**
   * Add a message to a specific agent's session
   * This prevents race conditions when the agent switches during message creation
   * @param agentId The agent to add the message to
   * @param message Message to add (without ID)
   * @returns Complete message with generated ID
   */
  addMessageForAgent(agentId: string, message: Omit<ChatMessage, "id">): ChatMessage {
    const newMessage: ChatMessage = {
      ...message,
      id: uuidv4(),
      timestamp: message.timestamp || Date.now(),
    };

    // Get the specific agent's session (create if doesn't exist)
    if (!this.sessions.has(agentId)) {
      this.sessions.set(agentId, {
        messages: [],
        lastUpdated: Date.now(),
        contractType: undefined,
        seenTipIds: new Set<string>(),
      });
    }

    const agentSession = this.sessions.get(agentId)!;

    // Add message to the specific agent's session
    this.sessions.set(agentId, {
      messages: [...agentSession.messages, newMessage],
      lastUpdated: Date.now(),
      contractType: agentSession.contractType,
      seenTipIds: agentSession.seenTipIds,
    });

    return newMessage;
  }

  /**
   * Update a message in a specific agent's session
   * This is critical for handling callbacks that execute after agent switches
   * @param agentId The agent whose session contains the message
   * @param id The message ID to update
   * @param updates The updates to apply to the message
   */
  updateMessageForAgent(
    agentId: string,
    id: string,
    updates: Partial<Omit<ChatMessage, "id">>
  ): void {
    const session = this.sessions.get(agentId);

    if (!session) {
      console.warn("MemorySessionManager: Agent session not found for update", {
        agentId,
        messageId: id,
      });
      return;
    }

    const messageIndex = session.messages.findIndex((msg) => msg.id === id);

    if (messageIndex !== -1) {
      // Create a new message object with updates
      const updatedMessage = {
        ...session.messages[messageIndex],
        ...updates,
        id, // Ensure ID doesn't change
      };

      // Create a new messages array
      const updatedMessages = [...session.messages];
      updatedMessages[messageIndex] = updatedMessage;

      // Update the specific agent's session
      this.sessions.set(agentId, {
        messages: updatedMessages,
        lastUpdated: Date.now(),
        contractType: session.contractType,
        seenTipIds: session.seenTipIds,
      });
    } else {
      console.warn("MemorySessionManager: Message not found in agent session", {
        messageId: id,
        agentId,
        messageCount: session.messages.length,
      });
    }
  }

  /**
   * Clear the current agent's session (preserves other agents' sessions)
   */
  clearSession(): void {
    const currentSession = this.getCurrentSession();
    // Preserve the contract type when clearing the session
    // Contract type is tied to the document, not the conversation
    const preservedContractType = currentSession.contractType;

    this.sessions.set(this.currentAgentId, {
      messages: [],
      lastUpdated: Date.now(),
      contractType: preservedContractType,
      seenTipIds: new Set<string>(), // Reset seen tips when conversation is cleared
    });

    // Reset the selected playbook on conversation reset (unlike contract type,
    // which is tied to the document and preserved above).
    this.selectedPlaybooks.delete(this.currentAgentId);
  }

  /**
   * Remove the last message pair (user + assistant messages) from the current agent's session
   * This is used when cancelling AI generation to clean up the conversation history
   */
  removeLastMessagePair(): void {
    const currentSession = this.getCurrentSession();
    const messages = [...currentSession.messages];

    // Find the last assistant message
    let lastAssistantIndex = -1;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === "assistant") {
        lastAssistantIndex = i;
        break;
      }
    }

    // Find the corresponding user message (should be right before the assistant message)
    let lastUserIndex = -1;
    if (lastAssistantIndex > 0) {
      for (let i = lastAssistantIndex - 1; i >= 0; i--) {
        if (messages[i].role === "user") {
          lastUserIndex = i;
          break;
        }
      }
    }

    // Remove both messages if found
    if (lastUserIndex >= 0 && lastAssistantIndex >= 0) {
      // Remove in reverse order to maintain indices
      messages.splice(lastAssistantIndex, 1);
      messages.splice(lastUserIndex, 1);

      // Create new session object with updated messages
      this.sessions.set(this.currentAgentId, {
        messages,
        lastUpdated: Date.now(),
        contractType: currentSession.contractType,
        seenTipIds: currentSession.seenTipIds,
      });
    }
  }

  /**
   * Remove the last message pair from a specific agent's session
   * This is used when cancelling AI generation for a specific agent in parallel scenarios
   * @param agentId The agent whose session to clean up
   */
  removeLastMessagePairForAgent(agentId: string): void {
    const session = this.sessions.get(agentId);
    if (!session) {
      log.warn("Cannot remove message pair - agent session not found", { agentId });
      return;
    }

    const messages = [...session.messages];

    // Find the last assistant message
    let lastAssistantIndex = -1;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === "assistant") {
        lastAssistantIndex = i;
        break;
      }
    }

    // Find the corresponding user message (should be right before the assistant message)
    let lastUserIndex = -1;
    if (lastAssistantIndex > 0) {
      for (let i = lastAssistantIndex - 1; i >= 0; i--) {
        if (messages[i].role === "user") {
          lastUserIndex = i;
          break;
        }
      }
    }

    // Remove both messages if found
    if (lastUserIndex >= 0 && lastAssistantIndex >= 0) {
      // Remove in reverse order to maintain indices
      messages.splice(lastAssistantIndex, 1);
      messages.splice(lastUserIndex, 1);

      // Create new session object with updated messages
      this.sessions.set(agentId, {
        messages,
        lastUpdated: Date.now(),
        contractType: session.contractType,
        seenTipIds: session.seenTipIds,
      });

      log.info("Removed last message pair for agent", {
        agentId,
        remainingMessages: messages.length,
      });
    }
  }

  /**
   * Get conversation history for the current agent in a format suitable for API requests
   *
   * This method augments the raw message content with contextual information that
   * helps the LLM understand what happened in previous turns:
   * - Interrupted responses are annotated
   * - Form displays are summarized so LLM knows what forms it triggered
   * - Submitted forms are noted so LLM knows the outcome
   * - Web searches are summarized with queries and results
   * - Playbook consultations are summarized with reason and available sections
   *
   * @returns Array of {role, content} objects
   */
  getConversationHistory(): { role: string; content: string | unknown[] }[] {
    const currentSession = this.getCurrentSession();
    const result: { role: string; content: string | unknown[] }[] = [];
    let pendingToolResults: unknown[] = [];

    for (const message of currentSession.messages) {
      // Flush any pending tool_results before a NON-user message. Two assistant
      // turns can be adjacent (e.g. a settled playbook-selection turn followed by
      // its continuation); without this flush the previous turn's trailing
      // tool_use would be left unclosed, and Anthropic rejects a tool_use that
      // isn't immediately followed by its tool_result.
      if (message.role !== "user" && pendingToolResults.length > 0) {
        result.push({ role: "user", content: pendingToolResults });
        pendingToolResults = [];
      }

      // If there are pending tool_result blocks from a previous assistant message,
      // merge them into this user message's content array
      if (message.role === "user" && pendingToolResults.length > 0) {
        result.push({
          role: "user",
          content: [...pendingToolResults, { type: "text", text: message.content }],
        });
        pendingToolResults = [];
        continue;
      }

      if (message.role !== "assistant") {
        result.push({ role: message.role, content: message.content });
        continue;
      }

      // --- Assistant message processing ---
      try {
        const interleaved = buildInterleavedMessages(message);
        log.debug("Interleaved assistant message", {
          messageId: message.id,
          outputMessages: interleaved.messages.length,
          hasTrailingToolResults: interleaved.trailingToolResults.length > 0,
        });
        for (const msg of interleaved.messages) {
          result.push(msg);
        }
        pendingToolResults = interleaved.trailingToolResults;
      } catch (err) {
        log.error("Failed to build interleaved history, falling back to plain text", {
          messageId: message.id,
          error: err,
        });
        result.push({ role: "assistant", content: message.content });
        pendingToolResults = [];
      }
    }

    // If pending tool_result blocks remain (assistant with tool_use was the last message),
    // append as a standalone user message. The BE will merge it with the current user message.
    if (pendingToolResults.length > 0) {
      result.push({ role: "user", content: pendingToolResults });
    }

    return result;
  }

  /**
   * Set the contract type for the current agent's session
   * @param contractType The contract type from classification
   */
  setContractType(contractType: string): void {
    const currentSession = this.getCurrentSession();
    this.sessions.set(this.currentAgentId, {
      messages: currentSession.messages,
      contractType,
      lastUpdated: Date.now(),
      seenTipIds: currentSession.seenTipIds,
    });
  }

  /**
   * Get the contract type for the current agent's session
   * @returns The contract type or null if not set
   */
  getContractType(): string | null {
    const currentSession = this.getCurrentSession();
    return currentSession.contractType || null;
  }

  /**
   * Set (or clear) the selected playbook for the current agent
   * @param playbook The chosen playbook, or null to clear the selection
   */
  setSelectedPlaybook(playbook: { id: string; name: string } | null): void {
    if (playbook) {
      this.selectedPlaybooks.set(this.currentAgentId, playbook);
    } else {
      this.selectedPlaybooks.delete(this.currentAgentId);
    }
  }

  /**
   * Get the selected playbook for the current agent
   * @returns The chosen playbook or null if none selected
   */
  getSelectedPlaybook(): { id: string; name: string } | null {
    return this.selectedPlaybooks.get(this.currentAgentId) ?? null;
  }

  // Learning tip tracking methods

  /**
   * Mark a tip as seen in the current agent's session
   * @param tipId The ID of the tip that was displayed
   */
  markTipAsSeen(tipId: string): void {
    const currentSession = this.getCurrentSession();
    currentSession.seenTipIds.add(tipId);
    log.debug("Tip marked as seen", {
      tipId,
      agentId: this.currentAgentId,
      seenCount: currentSession.seenTipIds.size,
    });
  }

  /**
   * Get the set of tip IDs that have been seen in the current agent's session
   * @returns Set of seen tip IDs
   */
  getSeenTipIds(): Set<string> {
    const currentSession = this.getCurrentSession();
    return currentSession.seenTipIds;
  }

  /**
   * Reset seen tips for the current agent's session
   * Called when all tips have been shown and we need to start the cycle over
   */
  resetSeenTips(): void {
    const currentSession = this.getCurrentSession();
    const previousCount = currentSession.seenTipIds.size;
    currentSession.seenTipIds.clear();
    log.info("Seen tips reset for session", {
      agentId: this.currentAgentId,
      previousSeenCount: previousCount,
    });
  }

  // Additional utility methods for multi-agent support

  /**
   * Clear all sessions across all agents
   * Use with caution - this removes all conversation history
   */
  clearAllSessions(): void {
    this.sessions.clear();
    this.selectedPlaybooks.clear();
  }

  /**
   * Get a specific agent's session
   * @param agentId The agent ID to get session for
   * @returns The agent's session or undefined if not found
   */
  getAgentSession(agentId: string): ChatSession | undefined {
    return this.sessions.get(agentId);
  }

  /**
   * Read a specific agent's session out in a serializable shape
   *
   * Flattens the two pieces of per-agent state that don't survive JSON on their
   * own: seenTipIds is a Set, and the selected playbook lives in a sidecar map
   * rather than on ChatSession. Messages are handed out by reference - callers
   * must not mutate them.
   *
   * @param agentId The agent ID to snapshot
   * @returns The agent's session state, or undefined if it has no session yet
   */
  getAgentSessionSnapshot(agentId: string): AgentSessionSnapshot | undefined {
    const session = this.sessions.get(agentId);
    if (!session) {
      return undefined;
    }

    return {
      contractType: session.contractType,
      selectedPlaybook: this.selectedPlaybooks.get(agentId) ?? null,
      seenTipIds: Array.from(session.seenTipIds),
      messages: session.messages,
    };
  }

  /**
   * Replace a specific agent's session wholesale
   *
   * Used to restore a stored conversation. This is a replace, not a merge: any
   * messages already in that agent's session are discarded, so it is meant to
   * run once at startup before the user can have said anything.
   *
   * @param agentId The agent whose session to replace
   * @param snapshot The session state to install
   */
  hydrateAgentSession(agentId: string, snapshot: AgentSessionSnapshot): void {
    const existing = this.sessions.get(agentId);
    if (existing && existing.messages.length > 0) {
      log.warn("Hydrating over an agent session that already has messages", {
        agentId,
        discardedMessages: existing.messages.length,
      });
    }

    this.sessions.set(agentId, {
      messages: snapshot.messages,
      lastUpdated: Date.now(),
      contractType: snapshot.contractType,
      seenTipIds: new Set(snapshot.seenTipIds),
    });

    if (snapshot.selectedPlaybook) {
      this.selectedPlaybooks.set(agentId, snapshot.selectedPlaybook);
    } else {
      this.selectedPlaybooks.delete(agentId);
    }

    log.info("Hydrated agent session", {
      agentId,
      messageCount: snapshot.messages.length,
      hasContractType: !!snapshot.contractType,
      hasSelectedPlaybook: !!snapshot.selectedPlaybook,
    });
  }

  /**
   * Check if an agent has an existing session with messages
   * @param agentId The agent ID to check
   * @returns true if the agent has a session with messages
   */
  hasAgentSession(agentId: string): boolean {
    const session = this.sessions.get(agentId);
    return session ? session.messages.length > 0 : false;
  }

  /**
   * Get list of all agents with active sessions
   * @returns Array of agent IDs that have sessions
   */
  getAgentsWithSessions(): string[] {
    return Array.from(this.sessions.keys()).filter((agentId) => {
      const session = this.sessions.get(agentId);
      return session && session.messages.length > 0;
    });
  }
}
